import type {
  PayloadFormQuestion,
  HostedFlowConfig,
  PayloadProgramHighlight,
} from "@portal/app/_server/services/formFunnel/payloadTypes";
import type { ProgramMetadata } from "@portal/app/_common/types/program";
import type { MatchScenario } from "../results/ResultScenarioRouter";
import type { MatchResult } from "../types";
import type { MultiResultOption } from "../results/MultiResultChooser";
import { deriveHostedFlowSteps } from "./deriveHostedFlowSteps";

export type { MatchResult };

type RichMatchItem = NonNullable<MatchResult["topMatch"]>;
type ClickoutScenario = Extract<MatchScenario, { type: "CLICKOUT" }>;
type HostedScenario = Extract<MatchScenario, { type: "HOSTED" }>;

interface MappingContext {
  targetProgramId: string | null | undefined;
  socialRedirectUrl: string | null | undefined;
  searchUrl: string;
  hostedFlowConfig?: HostedFlowConfig;
  funnelQuestions?: PayloadFormQuestion[];
  staticProgramHighlights?: PayloadProgramHighlight[];
}

const matchesFunnelProgram = (
  match: RichMatchItem,
  targetProgramId: string | null | undefined
): boolean => !targetProgramId || match.program.id === targetProgramId;

const shouldShowStaticProgramHighlights = (
  match: RichMatchItem,
  context: MappingContext
): boolean =>
  !!context.staticProgramHighlights?.length &&
  !!context.targetProgramId &&
  matchesFunnelProgram(match, context.targetProgramId);

const hasFinancialAidOption = (match: RichMatchItem): boolean =>
  match.program.paymentOptions?.some((po) => po.isActive) ?? false;

const mapClickoutMatch = (
  match: RichMatchItem,
  context: MappingContext,
  prePingResult?: unknown
): ClickoutScenario => {
  const shouldUseFunnelAuthoredCopy = matchesFunnelProgram(match, context.targetProgramId);
  return {
    type: "CLICKOUT",
    program: {
      id: match.program.id,
      name: match.program.name,
      slug: match.program.slug,
      environment: match.program.environment,
      leadRedirect:
        shouldUseFunnelAuthoredCopy && context.socialRedirectUrl
          ? context.socialRedirectUrl
          : match.program.preLeadRedirect,
      preLeadRedirectParams: match.program.preLeadRedirectParams,
      preLeadRedirectCompositeParams: match.program
        .preLeadRedirectCompositeParams as ProgramMetadata["preLeadRedirectCompositeParams"],
      preLeadRedirectSuppressDefaultParams: match.program.preLeadRedirectSuppressDefaultParams,
      customCtaCopy: match.program.customCtaCopy,
      customDisclaimer: match.program.customDisclaimer,
      programHighlights: shouldUseFunnelAuthoredCopy ? match.program.programHighlights : [],
      hasFinancialAidOption: hasFinancialAidOption(match),
      provider: {
        id: match.provider.id,
        slug: match.provider.slug,
        name: match.provider.fullName,
        shortName: match.provider.shortName,
        advertiserName: match.provider.advertiserName || match.provider.fullName,
        isTrustedFormEnabled: match.provider.isTrustedFormEnabled,
        logoImageFile: match.provider.logoImageFile,
      },
    },
    staticProgramHighlights: shouldShowStaticProgramHighlights(match, context)
      ? context.staticProgramHighlights
      : undefined,
    prePingResult: prePingResult ?? undefined,
  };
};

const mapHostedMatch = (
  match: RichMatchItem,
  context: MappingContext,
  prePingResult?: unknown
): HostedScenario => {
  const shouldUseFunnelAuthoredCopy = matchesFunnelProgram(match, context.targetProgramId);
  const derivedSteps = deriveHostedFlowSteps(
    match.program.requiredFields,
    context.hostedFlowConfig?.steps,
    match.program.dobFieldVisibility
  );
  const mappedHostedFlowConfig = shouldUseFunnelAuthoredCopy
    ? {
        ...context.hostedFlowConfig,
        steps: derivedSteps,
      }
    : {
        steps: derivedSteps,
        postLeadRedirectMode: context.hostedFlowConfig?.postLeadRedirectMode,
        postLeadRedirectDelaySeconds: context.hostedFlowConfig?.postLeadRedirectDelaySeconds,
      };

  return {
    type: "HOSTED",
    program: {
      id: match.program.id,
      name: match.program.name,
      environment: match.program.environment,
      programHighlights: shouldUseFunnelAuthoredCopy ? match.program.programHighlights : [],
      provider: {
        id: match.provider.id,
        shortName: match.provider.shortName,
        fullName: match.provider.fullName,
        advertiserName: match.provider.advertiserName || match.provider.fullName,
        isTrustedFormEnabled: match.provider.isTrustedFormEnabled,
        successPageMessage: match.provider.successPageMessage,
        logoImageFile: match.provider.logoImageFile,
      },
      customCtaCopy: match.program.customCtaCopy,
      customDisclaimer: match.program.customDisclaimer,
      allowedZipCodes: match.program.allowedZipCodes,
      dobFieldVisibility: match.program.dobFieldVisibility,
      hsGradYearMin: match.program.hsGradYearMin,
      hsGradYearMax: match.program.hsGradYearMax,
      vocation: match.program.vocation,
      hasFinancialAidOption: hasFinancialAidOption(match),
    },
    hostedFlowConfig: mappedHostedFlowConfig,
    funnelQuestions: context.funnelQuestions,
    staticProgramHighlights: shouldShowStaticProgramHighlights(match, context)
      ? context.staticProgramHighlights
      : undefined,
    prePingResult: prePingResult ?? undefined,
    searchUrl: context.searchUrl,
  };
};

const mapRankedMatch = (
  result: NonNullable<MatchResult["resultMatches"]>[number],
  context: MappingContext
): MultiResultOption | null => {
  // `socialRedirectUrl` is a funnel-level campaign destination authored for the single
  // recommendation a funnel is expected to surface. On an untargeted funnel (no
  // `targetProgramId`) every ranked match "matches" the funnel program, so applying it
  // to each card would send rank 2+ clickouts to the rank-1 school's URL. Scope it to
  // the top card, which preserves the single-result behavior the field was authored for.
  const rankedContext: MappingContext =
    result.rank === 1 ? context : { ...context, socialRedirectUrl: null };

  if (result.scenario === "CLICKOUT") {
    return {
      ...mapClickoutMatch(result.match, rankedContext, result.prePingResult),
      rank: result.rank,
    };
  }

  if (result.scenario === "HOSTED") {
    return {
      ...mapHostedMatch(result.match, rankedContext, result.prePingResult),
      rank: result.rank,
    };
  }

  return null;
};

/**
 * Maps the GraphQL response to the MatchScenario type expected by ResultScenarioRouter.
 */
export const mapResponseToScenario = (
  result: MatchResult,
  fallbackRedirectUrl: string,
  hostedFlowConfig?: HostedFlowConfig,
  funnelQuestions?: PayloadFormQuestion[],
  staticProgramHighlights?: PayloadProgramHighlight[]
): MatchScenario => {
  const {
    scenario,
    topMatch,
    redirectUrl,
    matches,
    targetProgramId,
    targetProgramName,
    socialRedirectUrl,
    prePingResult,
    resultMatches,
    maxResults,
  } = result;

  const context: MappingContext = {
    targetProgramId,
    socialRedirectUrl,
    searchUrl: redirectUrl || fallbackRedirectUrl,
    hostedFlowConfig,
    funnelQuestions,
    staticProgramHighlights,
  };

  if ((maxResults ?? 1) > 1 && resultMatches?.length) {
    const rankedResults = resultMatches
      .map((rankedMatch) => mapRankedMatch(rankedMatch, context))
      .filter((rankedMatch): rankedMatch is MultiResultOption => rankedMatch !== null);

    if (rankedResults.length > 0) {
      return {
        type: "MULTI_RESULT",
        results: rankedResults,
      };
    }
  }

  if (scenario === "CLICKOUT" && topMatch) {
    return mapClickoutMatch(topMatch, context, prePingResult);
  }

  if (scenario === "HOSTED" && topMatch) {
    return mapHostedMatch(topMatch, context, prePingResult);
  }

  if (scenario === "PROGRAM_UNAVAILABLE") {
    // Filter out the target program from similar programs by ID to avoid showing it
    // as a suggestion when it's the program that is unavailable.
    // Uses targetProgramId (not name) for robust matching -- program names are not unique.
    const similarPrograms = matches
      ?.filter((m) => (targetProgramId ? m.program.id !== targetProgramId : true))
      .slice(0, 5)
      .map((m) => ({
        id: m.program.id,
        name: m.program.name,
        providerName: m.provider.fullName,
        providerSlug: m.provider.slug,
        slug: m.program.slug,
      }));

    return {
      type: "PROGRAM_UNAVAILABLE",
      programName: targetProgramName ?? undefined,
      searchUrl: redirectUrl || fallbackRedirectUrl,
      similarPrograms: similarPrograms?.length ? similarPrograms : undefined,
    };
  }

  // NO_MATCH scenario or fallback
  const similarPrograms = matches?.slice(0, 5).map((m) => ({
    id: m.program.id,
    name: m.program.name,
    providerName: m.provider.fullName,
    providerSlug: m.provider.slug,
    slug: m.program.slug,
  }));

  return {
    type: "NO_MATCH",
    searchUrl: redirectUrl || fallbackRedirectUrl,
    similarPrograms: similarPrograms?.length ? similarPrograms : undefined,
  };
};
